import { motion } from "framer-motion";
import { useEffect } from "react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_melting-moments-1/artifacts/mfe7l5w4_Screenshot%202026-04-14%20at%208.10.43%20PM.png";

const ease = [0.16, 1, 0.3, 1];

export default function MissionVision() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#050505] min-h-screen noise-overlay">
      {/* Navbar */}
      <nav
        data-testid="mission-navbar"
        className="fixed top-0 w-full z-50 backdrop-blur-xl bg-[#050505]/80 border-b border-[#1F1F1F]"
      >
        <div className="flex justify-between items-center py-4 px-6 md:px-12 lg:px-24">
          <Link to="/" data-testid="mission-nav-logo" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="YY Atelier" className="h-10 md:h-12 w-auto object-contain mix-blend-lighten" />
          </Link>
          <Link
            to="/"
            data-testid="mission-nav-back"
            className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500"
          >
            Back to Home
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 px-6 md:px-12 lg:px-24 text-center">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, ease }}
          className="font-body text-xs md:text-sm uppercase tracking-[0.2em] text-[#C6A96B] mb-6"
        >
          What We Stand For
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.2, ease }}
          data-testid="mission-vision-title"
          className="font-heading text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter text-[#FDFBF7] mb-8"
        >
          Mission & Vision
        </motion.h1>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: 80 }}
          transition={{ duration: 1, delay: 0.5, ease }}
          className="h-px bg-[#C6A96B] mx-auto"
        />
      </section>

      {/* Split Layout — Mission & Vision */}
      <section
        data-testid="mission-vision-split"
        className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
            data-testid="mission-block"
            className="border border-[#1F1F1F] p-10 md:p-16 lg:p-20 relative group"
          >
            {/* Subtle duality shape */}
            <div className="absolute top-8 right-8 w-8 h-8 opacity-[0.08]">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19" stroke="#C6A96B" strokeWidth="1"/>
                <path d="M20 1C20 1 20 39 20 39" stroke="#C6A96B" strokeWidth="0.5"/>
                <circle cx="20" cy="12" r="4" fill="#C6A96B" fillOpacity="0.3"/>
                <circle cx="20" cy="28" r="4" stroke="#C6A96B" strokeWidth="0.5"/>
              </svg>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2, ease }}
              className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B] mb-8"
            >
              01 — Mission
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.3, ease }}
              data-testid="mission-heading"
              className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#FDFBF7] mb-10"
            >
              Our Mission
            </motion.h2>

            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 40 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.5, ease }}
              className="h-px bg-[#C6A96B] mb-10"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.6, ease }}
              className="font-heading text-xl md:text-2xl font-light italic text-[#FDFBF7]/80 mb-8 leading-relaxed"
            >
              To redefine indulgence through contrast.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.8, ease }}
              className="font-body text-base font-light text-[#A3A3A3] leading-relaxed mb-6"
            >
              At YY Atelier, we craft chocolate as a paired experience — where every
              creation is designed to be experienced together, not alone.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 1, ease }}
              className="font-body text-base font-light text-[#A3A3A3] leading-relaxed"
            >
              Through a balance of flavours, textures, and emotions, we transform
              chocolate into a moment of harmony.
            </motion.p>
          </motion.div>

          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, delay: 0.15, ease }}
            data-testid="vision-block"
            className="border border-[#1F1F1F] border-t-0 md:border-t md:border-l-0 p-10 md:p-16 lg:p-20 relative group"
          >
            {/* Subtle duality shape — inverted */}
            <div className="absolute top-8 right-8 w-8 h-8 opacity-[0.08]">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="19" stroke="#C6A96B" strokeWidth="1"/>
                <path d="M20 1C20 1 20 39 20 39" stroke="#C6A96B" strokeWidth="0.5"/>
                <circle cx="20" cy="12" r="4" stroke="#C6A96B" strokeWidth="0.5"/>
                <circle cx="20" cy="28" r="4" fill="#C6A96B" fillOpacity="0.3"/>
              </svg>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.35, ease }}
              className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B] mb-8"
            >
              02 — Vision
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.45, ease }}
              data-testid="vision-heading"
              className="font-heading text-4xl md:text-5xl font-light tracking-tight text-[#FDFBF7] mb-10"
            >
              Our Vision
            </motion.h2>

            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: 40 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.6, ease }}
              className="h-px bg-[#C6A96B] mb-10"
            />

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.7, ease }}
              className="font-heading text-xl md:text-2xl font-light italic text-[#FDFBF7]/80 mb-8 leading-relaxed"
            >
              To become the most distinctive luxury chocolate atelier in the world.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 0.9, ease }}
              className="font-body text-base font-light text-[#A3A3A3] leading-relaxed mb-6"
            >
              We envision a future where indulgence is not about choosing one, but
              experiencing both — where every product reflects balance, intention, and
              refined craftsmanship.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, delay: 1.1, ease }}
              className="font-body text-base font-light text-[#A3A3A3] leading-relaxed"
            >
              Every pair we create is an invitation to slow down, savour, and share
              something meaningful with someone who matters.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values strip */}
      <section
        data-testid="values-section"
        className="px-6 md:px-12 lg:px-24 pb-24 md:pb-32"
      >
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease }}
            className="text-center mb-16"
          >
            <p className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B] mb-4">
              Our Principles
            </p>
            <div className="w-8 h-px bg-[#C6A96B] mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#1F1F1F]">
            {[
              { num: "I", title: "Duality", text: "Every creation exists in two. Contrast is not opposition — it is completion." },
              { num: "II", title: "Craft", text: "Single-origin cacao, hand-tempered in silence. No shortcuts, no compromise." },
              { num: "III", title: "Togetherness", text: "Chocolate meant to be shared. A ritual of connection, not consumption." },
            ].map((value, i) => (
              <motion.div
                key={value.title}
                data-testid={`value-${value.title.toLowerCase()}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.15, ease }}
                className="bg-[#050505] p-10 md:p-12 text-center"
              >
                <span className="font-heading text-2xl italic text-[#C6A96B]/40 mb-6 block">
                  {value.num}
                </span>
                <h3 className="font-heading text-2xl font-light text-[#FDFBF7] mb-4 tracking-normal">
                  {value.title}
                </h3>
                <div className="w-6 h-px bg-[#C6A96B] mx-auto mb-6" />
                <p className="font-body text-sm font-light text-[#A3A3A3] leading-relaxed">
                  {value.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing manifesto */}
      <section className="px-6 md:px-12 lg:px-24 pb-28 md:pb-36">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C6A96B] to-transparent mx-auto mb-12" />
            <p
              data-testid="manifesto-text"
              className="font-heading text-2xl md:text-3xl font-light italic text-[#FDFBF7]/70 leading-relaxed mb-12"
            >
              "The most beautiful things in life were never meant to stand alone."
            </p>
            <div className="w-12 h-px bg-[#C6A96B] mx-auto mb-8" />
            <p className="font-body text-xs uppercase tracking-[0.3em] text-[#C6A96B]/60">
              YY Atelier
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1F1F1F] py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="font-body text-xs uppercase tracking-[0.2em] text-[#A3A3A3] hover:text-[#C6A96B] transition-colors duration-500">
            Return Home
          </Link>
          <p className="font-body text-xs text-[#A3A3A3]/50 tracking-wider">
            &copy; {new Date().getFullYear()} YY Atelier. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
